import { getModule as getStoreModule, Module, Mutation, VuexModule } from 'vuex-module-decorators';
import { Action } from '@libraries/vuex-module-decorators/Action';
import PresentationService from '@app/services/presentations/PresentationService';
import IChapterMaster from '@app/types/presentations/master/IChapterMaster';
import ISlideView from '@app/types/ISlideView';
import IPresentation from '@app/types/presentations/presentation/IPresentation';
import PresentationMasterService from '@app/services/presentations/PresentationMasterService';
import CustomSlideService from '@app/services/presentations/CustomSlideService';
import IPresentationMaster from '@app/types/presentations/master/IPresentationMaster';
import IPresentationSettings from '@app/types/presentations/presentation/IPresentationSettings';
import IChapterItem from '@app/types/presentations/IChapterItem';
import ChapterItemType from '@app/types/presentations/ChapterItemType';
import SystemOfUnits from '@app/types/base/SystemOfUnits';
import UserStoreModule from '@app/store/modules/UserStoreModule';
import IRawContact from '@app/types/base/IRawContact';
import GlobalsStoreModule from '@app/store/modules/GlobalsStoreModule';
import ISlideSettings from '@app/types/presentations/presentation/ISlideSettings';
import SlideTransform from '@app/types/base/SlideTransform';
import ISlideMaster from '@app/types/presentations/master/ISlideMaster';
import Vue from 'vue';
import IStructuredPresentation, {
	IStructuredChapter,
	IStructuredChapterItem,
	IStructuredSection,
	IStructuredSlide
} from '@app/types/presentations/IStructuredPresentation';
import SlideType from '@app/types/presentations/SlideType';
import ICustomSlide from '@app/types/presentations/presentation/ICustomSlide';
import ChapterType from '@app/types/presentations/ChapterType';
import store from '@app/store/store';
import AssetCacheService from '@app/services/cache/AssetCacheService';
import Freshness from '@app/types/presentations/Freshness';
import IChapterSettings from '@app/types/presentations/presentation/IChapterSettings';
import StateCacheService from '@/app/src/services/cache/StateCacheService';
import PresentationMasterSnapshotService from '@/app/src/services/presentations/PresentationMasterSnapshotService';
import PresentationMasterUtil from '@app/utils/PresentationMasterUtil';
import PresentationUtil from '@app/utils/PresentationUtil';
import SlideUtil from '@app/utils/SlideUtil';
import OfflineError, {
	isNetworkOrOfflineErrorAndAppIsOffline,
	throwIfNotOfflineError
} from '@app/types/errors/OfflineError';
import InvalidMasterSnapshotError from '@app/types/errors/InvalidMasterSnapshotError';
import {
	throwCacheErrorIfUnsupportedInClient,
	throwIfNotCachingUnsupportedInClient
} from '@app/types/errors/CacheError';
import AuthenticationError from '@app/types/errors/AuthenticationError';
import PowerPointExportService from '@app/services/export/ppt/PowerPointExportService';
import MarketRestrictionUtil from '@app/utils/MarketRetrictionUtil';
import ResourceNotFoundError from '@app/types/errors/ResourceNotFoundError';
import { User } from 'contentful-management';
import AppStoreModule from './AppStoreModule';

const moduleId = 'presentation';

/**
 * Function which transforms the slides. Will be set from outside the store to avoid cycle dependencies
 *
 * Please also note that this is no property of the store because the members variables represent the state keys,
 * which should stay JSON compatible.
 */
let transformSlideFunction: SlideTransform = (): any => ({});

export function getSlideTransformFunction(): SlideTransform {
	return transformSlideFunction;
}

const getPresentationMasterCacheId = (id: string, checksum: string): string => `${id}.${checksum}`;

const appStore: AppStoreModule = getStoreModule(AppStoreModule);
const userStore: UserStoreModule = getStoreModule(UserStoreModule);
const globalsStore: GlobalsStoreModule = getStoreModule(GlobalsStoreModule);

@Module({
	name: moduleId,
	store,
	dynamic: true,
	namespaced: true
})
/**
 * Presentation store module
 * Handles a single presentation by loading it from Contentful and providing access to its chapters, sections, slides and settings
 *
 * * All Ids used are Contentful Entry Ids *
 */
export default class PresentationStoreModule extends VuexModule {
	/**
	 * Presentation master
	 */
	public presentationsById: Record<string, IPresentation> = {};

	/**
	 * Current Presentation master
	 */
	public currentMastersByIdAndLocale: Record<string, Record<string, IPresentationMaster>> = {};

	/**
	 * Presentation master snapshots
	 */
	public masterSnapshotsByMasterIdChecksumAndLocale: Record<string, Record<string, Record<string, IPresentationMaster>>> = {};

	/**
	 * Dictionary of slideId -> Promise<ISlideView>
	 */
	public slideViewPromisesById: Record<string, Promise<ISlideView>> = {};

	/**
	 * Dictionary of slideId -> ISlideView
	 */
	public slideViewsById: Record<string, ISlideView> = {};

	/**
	 * Current Slide id
	 */
	public currentSlideId: string = '';

	/**
	 * Partial PresentationSettings that are used to override presentation settings on demand,
	 * e.g. systemOfUnits, locale, ...
	 *
	 * Joined with current presentations's settings in `get settingsWithOverrides()`
	 */
	public overridePresentationSettings: Partial<IPresentationSettings> = {};

	/**
	 * Dictionary of presentationId -> Asset Loading Promises
	 */
	public assetLoadingPromisesByPresentationIdAndUrl: Record<string, Record<string, Promise<void>>> = {};

	/**
	 * Dictionary of presentationId -> Asset cache offload in % (0-1)
	 */
	public assetCacheOffloadsByPresentationId: Record<string, number> = {};

	/**
	 * Checksums of presentations that were cached for offline use
	 */
	public cachedPresentationsChecksumsById: Record<string, string> = {};

	/**
	 * Current presentation id
	 */
	public presentationId: string = '';

	@Action
	public async saveState(): Promise<void> {
		throwCacheErrorIfUnsupportedInClient();

		const presentationStateCacheService: StateCacheService = new StateCacheService('presentations');
		const presentationMasterStateCacheService: StateCacheService = new StateCacheService('presentationMasters');

		const previousPresentationKeys: string[] = await presentationStateCacheService.keys();
		const previousPresentationMasterKeys: string[] = await presentationMasterStateCacheService.keys();
		const currentPresentationKeys: string[] = [];
		const currentPresentationMasterKeys: string[] = [];

		// build a list of all master snapshots actually in use to save only what is still needed
		const presentationMasterIdsAndChecksumsInUse: string[] = [];

		await Promise.all(
			Object.values(this.presentationsById)
				// store only presentations of the current user
				.filter(
					(presentation: IPresentation): boolean =>
						!!userStore.currentUser && presentation.ownerId === (userStore.currentUser as User).sys.id
				)
				// store only presentations for a master snapshot exists locally
				.filter(
					(presentation: IPresentation): boolean =>
						!!this.masterSnapshotsByMasterIdChecksumAndLocale[presentation.masterId] &&
						!!this.masterSnapshotsByMasterIdChecksumAndLocale[presentation.masterId][presentation.masterChecksum]
				)
				.map(
					(presentation: IPresentation): Promise<any> => {
						currentPresentationKeys.push(presentation.id);
						presentationMasterIdsAndChecksumsInUse.push(
							getPresentationMasterCacheId(presentation.masterId, presentation.masterChecksum)
						);

						return presentationStateCacheService.save(presentation.id, { ...presentation });
					}
				)
		);

		const presentationMasterSavePromises: Promise<any>[] = [];

		Object.keys(this.masterSnapshotsByMasterIdChecksumAndLocale).forEach((id: string): void => {
			Object.keys(this.masterSnapshotsByMasterIdChecksumAndLocale[id]).forEach((checksum: string): void => {
				const cacheId: string = getPresentationMasterCacheId(id, checksum);
				currentPresentationMasterKeys.push(cacheId);

				// exclude snapshot from cache if it is no longer needed
				if (!presentationMasterIdsAndChecksumsInUse.includes(cacheId)) return;

				presentationMasterSavePromises.push(
					presentationMasterStateCacheService.save(
						cacheId,
						this.masterSnapshotsByMasterIdChecksumAndLocale[id][checksum]
					)
				);
			});
		});

		await Promise.all(presentationMasterSavePromises);

		// remove obsolete presentations
		await Promise.all(
			previousPresentationKeys
				.filter((key: string): boolean => !currentPresentationKeys.includes(key))
				.map((key: string): Promise<void> => presentationStateCacheService.remove(key))
		);

		// remove obsolete presentation master snapshots
		await Promise.all(
			previousPresentationMasterKeys
				.filter((key: string): boolean => !currentPresentationMasterKeys.includes(key))
				.map((key: string): Promise<void> => presentationMasterStateCacheService.remove(key))
		);
	}

	@Action
	public async restoreState(): Promise<void> {
		throwCacheErrorIfUnsupportedInClient();

		const presentationStateCacheService: StateCacheService = new StateCacheService('presentations');
		const presentationKeys: string[] = await presentationStateCacheService.keys();
		const presentationMasterStateCacheService: StateCacheService = new StateCacheService('presentationMasters');
		const presentationMasterKeys: string[] = await presentationMasterStateCacheService.keys();

		if (presentationKeys.length > 0 && presentationMasterKeys.length > 0) {
			await Promise.all(
				presentationMasterKeys.map(
					async (key: string): Promise<any> => {
						const presentationMastersByLocale: Record<string, IPresentationMaster> = await presentationMasterStateCacheService.restore(key); // eslint-disable-line
						Object.values(presentationMastersByLocale).forEach((presentationMaster: IPresentationMaster): void => {
							this.setPresentationMasterSnapshot(presentationMaster as IPresentationMaster);
						});

						console.debug(`[PresentationStoreModule] Restored presentation master snapshot ${key} from cache.`);
					}
				)
			);
			await Promise.all(
				presentationKeys.map(
					async (key: string): Promise<any> => {
						await this.registerPresentationIfMasterExists((await presentationStateCacheService.restore(
							key
						)) as IPresentation);
					}
				)
			);

			return;
		}

		// Legacy support for all store modules stored in a single table
		const cache: StateCacheService = new StateCacheService();
		const cachedState: Record<string, any> | null = await cache.restore(moduleId);
		if (!cachedState) return;

		if (
			cachedState.masterSnapshotsByMasterIdChecksumAndLocale &&
			Object.values(cachedState.masterSnapshotsByMasterIdChecksumAndLocale).length
		) {
			const masterSnapshotsByMasterIdChecksumAndLocale: Record<string, Record<string, Record<string, IPresentationMaster>>> = cachedState.masterSnapshotsByMasterIdChecksumAndLocale || {};

			Object.values(masterSnapshotsByMasterIdChecksumAndLocale).forEach(
				(mastersByChecksumAndLocale: Record<string, Record<string, IPresentationMaster>>): void => {
					Object.values(mastersByChecksumAndLocale).forEach(
						(mastersByLocale: Record<string, IPresentationMaster>): void => {
							Object.values(mastersByLocale).forEach((master: IPresentationMaster): void =>
								this.setPresentationMasterSnapshot(master)
							);
						}
					);
				}
			);
		}

		if (cachedState.presentationsById && Object.values(cachedState.presentationsById).length) {
			(Object.values(cachedState.presentationsById) as IPresentation[]).forEach((presentation: IPresentation): void => {
				// SALES-MANUAL-555 start
				// fallback for old presentations which chapters id array (chapters: string[])
				if (typeof presentation.settings.chapters[0] === 'string') {
					// eslint-disable-next-line no-param-reassign
					presentation.settings.chapters = ((presentation.settings.chapters as unknown) as string[]).map(
						(chapterId: string) => ({
							id: chapterId,
							showIntroVideo: true
						})
					);
				}
				// SALES-MANUAL-555 end
				this.registerPresentationIfMasterExists(presentation);
			});
		}
	}

	@Action
	public async saveStateIfSupported(): Promise<void> {
		return this.saveState().catch(throwIfNotCachingUnsupportedInClient);
	}

	@Action
	public async restoreStateIfSupported(): Promise<void> {
		return this.restoreState().catch(throwIfNotCachingUnsupportedInClient);
	}

	/**
	 * Go to slide with given id.
	 * We will await the slides transformation promise to complete before
	 * proceeding to the requested slide
	 */
	@Action
	public async goToSlide(slideId: string): Promise<void> {
		if (!this.slideViewPromisesById[slideId]) throw new Error(`Unknown Slide id: ${slideId}`);
		await this.slideViewPromisesById[slideId];
		this.setCurrentSlideId(slideId);
	}

	/**
	 * Load a presentation master its Contentful id and locale
	 */
	@Action
	public async loadCurrentPresentationMaster(id: string): Promise<void> {
		try {
			if (!appStore.isOnline) throw new OfflineError();

			const presentationMasterService: PresentationMasterService = new PresentationMasterService(globalsStore.locales);
			const presentationMastersByLocale: Record<string, IPresentationMaster> = await presentationMasterService.get(id);

			Object.values(presentationMastersByLocale).forEach((presentationMaster: IPresentationMaster): void =>
				this.setCurrentPresentationMaster(presentationMaster)
			);

			console.debug(
				`[PresentationStoreModule] Current master ${id} / ${presentationMastersByLocale[process.env.PRESENTATION_DEFAULT_LOCALE as string].checksum} loaded.`
			);
		} catch (error) {
			if (isNetworkOrOfflineErrorAndAppIsOffline(error)) {
				throw new OfflineError('Cannot load presentation master as application is offline');
			}
		}
	}

	/**
	 * Load a presentation master snapshot by its master id and checksum
	 */

	@Action
	public async loadPresentationMasterSnapshotIfNotAlreadyLoaded({
		masterId,
		checksum
	}: {
		masterId: string;
		checksum: string;
	}): Promise<void> {
		// master snapshots are immutable, so no need to load what already exists in the store
		if (
			PresentationMasterUtil.findMasterSnapshotsByIdAndChecksum(
				masterId,
				checksum,
				this.masterSnapshotsByMasterIdChecksumAndLocale
			)
		) {
			return;
		}

		try {
			if (!appStore.isOnline) {
				throw new OfflineError();
			}

			const presentationMasterSnapshotService: PresentationMasterSnapshotService = new PresentationMasterSnapshotService(
				globalsStore.locales
			);

			const presentationMastersByLocale: Record<string, IPresentationMaster> = await presentationMasterSnapshotService.getByMasterIdAndChecksum(masterId, checksum);

			Object.values(presentationMastersByLocale).forEach((presentationMaster: IPresentationMaster): void =>
				this.setPresentationMasterSnapshot(presentationMaster)
			);

			console.debug(`[PresentationStoreModule] Master snapshot ${masterId} / ${checksum} loaded.`);
		} catch (error) {
			if (isNetworkOrOfflineErrorAndAppIsOffline(error)) {
				// application went offline during network requests
				throw new OfflineError('Cannot load master snapshot - application is offline');
			}
			throw error;
		}
	}

	@Action
	public async ensureSnapshotOfCurrentMasterExists(id: string): Promise<void> {
		const presentationMasterService: PresentationMasterService = new PresentationMasterService(globalsStore.locales);
		const presentationMasterSnapshotService: PresentationMasterSnapshotService = new PresentationMasterSnapshotService(
			globalsStore.locales
		);

		// if a snapshot of the current master is already registered no remote tests are necessary
		if (
			(!!this.masterSnapshotsByMasterIdChecksumAndLocale[id] &&
				!!this.masterSnapshotsByMasterIdChecksumAndLocale[id][
					this.currentMastersByIdAndLocale[id][process.env.PRESENTATION_DEFAULT_LOCALE as string].checksum
				]) ||
			!appStore.isOnline
		) {
			return;
		}

		try {
			if (!appStore.isOnline) throw new OfflineError();

			let masterSnapshotsByLocale: Record<string, IPresentationMaster>;

			try {
				masterSnapshotsByLocale = await presentationMasterSnapshotService.getByMasterIdAndChecksum(
					id,
					this.currentMastersByIdAndLocale[id][process.env.PRESENTATION_DEFAULT_LOCALE as string].checksum
				);
			} catch (error) {
				if (error instanceof ResourceNotFoundError) {
					const rawMaster = await presentationMasterService.getRaw(id);
					masterSnapshotsByLocale = await presentationMasterSnapshotService.create(rawMaster);
				} else {
					throw error;
				}
			}

			Object.values(masterSnapshotsByLocale).forEach((masterSnapshot: IPresentationMaster): void =>
				this.setPresentationMasterSnapshot(masterSnapshot)
			);
		} catch (error) {
			if (isNetworkOrOfflineErrorAndAppIsOffline(error)) {
				throw new OfflineError('Cannot process master snapshot while offline');
			}

			throw error;
		}
	}

	/**
	 * Load a presentation master its Contentful id and locale
	 */

	@Action
	public async loadCurrentMasters(): Promise<void> {
		try {
			if (!appStore.isOnline) throw new OfflineError();

			const presentationMasterService: PresentationMasterService = new PresentationMasterService(globalsStore.locales);
			(await presentationMasterService.getAll()).forEach((presentationMaster: IPresentationMaster): void => {
				this.setCurrentPresentationMaster(presentationMaster);
			});
		} catch (error) {
			if (isNetworkOrOfflineErrorAndAppIsOffline(error)) {
				throw new OfflineError('Cannot load presentation masters while application is offline');
			}
			throw error;
		}
	}

	@Action
	public async initPresentationMasterFreshness(): Promise<void> {
		return this.loadCurrentMasters();
	}

	/**
	 * Load settings for a presentations and it's master
	 */
	@Action
	public async loadPresentation(id: string): Promise<void> {
		try {
			if (!appStore.isOnline) throw new OfflineError();
			const presentationService: PresentationService = new PresentationService();
			const presentation: IPresentation = await presentationService.get(id);

			// if contacts are configured, validate if at least one contact still exists.
			// prevent use of the presentation if no contacts are left
			// @see https://jungfrau.atlassian.net/browse/SALESAPP-1606
			if (presentation.settings.contactIds.length > 0) {
				const existingContacts = presentation.settings.contactIds.map((contactId: string) =>
					globalsStore.contacts.find((contact: IRawContact) => contact.id === contactId)
				);
				if (existingContacts.length === 0) {
					throw new ResourceNotFoundError(
						'Presentation can no longer be used as it is lacking required contact information'
					);
				}
			}

			// presentations cannot be used without their master, so make sure it is loaded
			await this.loadPresentationMasterSnapshotIfNotAlreadyLoaded({
				masterId: presentation.masterId,
				checksum: presentation.masterChecksum
			});

			await this.registerPresentation(presentation);
		} catch (error) {
			if (isNetworkOrOfflineErrorAndAppIsOffline(error) && !!this.presentationsById[id]) {
				throw new OfflineError('Presentation not loaded as application is offline');
			} else {
				throw error;
			}
		}
	}

	/**
	 * Load settings for all presentations and their masters
	 */
	@Action
	public async loadAllPresentations(): Promise<void> {
		try {
			if (!appStore.isOnline) throw new OfflineError();

			await userStore.loadCurrentUser();
			const presentationService: PresentationService = new PresentationService();
			const query: Record<string, any> = {};

			// temporary disabled latest presentations, by always restricting load to current user presentations
			// to be replaced by solution to be defined in SALESAPP-586
			// if (userStore.currentUser && userStore.currentUser.role === Role.SalesRepresentative)
			if (userStore.currentUser) query['fields.ownerId'] = userStore.currentUser.sys.id;

			const presentations: IPresentation[] = await presentationService.getAll(undefined, query);

			// collect all local presentations that do not exist remotely so we may cleanup our local state
			const presentationsToRemove: IPresentation[] = this.presentations.filter(
				(localPresentation: IPresentation): boolean =>
					!presentations.find(
						(loadedPresentation: IPresentation): boolean => loadedPresentation.id === localPresentation.id
					)
			);

			// register presentations sequentially to allow masters to be fetched only once
			for (const presentation of presentations) { // eslint-disable-line no-restricted-syntax
				await this.registerPresentationIfMasterExists(presentation); // eslint-disable-line no-await-in-loop
			}

			// remove all presentations that exist locally, but not remote
			presentationsToRemove.forEach((presentation: IPresentation): void => {
				this.removePresentationById(presentation.id);
			});
		} catch (error) {
			if (isNetworkOrOfflineErrorAndAppIsOffline(error)) {
				throw new OfflineError('Cannot load presentations - application is offline');
			} else {
				throw error;
			}
		}
	}

	/**
	 * Load an initialize store module with a presentation by its Contentful id
	 * Will initialize all state properties and reset the current slide to the first slide of the presentation
	 */

	@Action
	public async initPresentationByIdAndStartTransforms(id: string): Promise<void> {
		if (!this.presentationsById[id]) {
			await this.loadPresentation(id);
		}

		if (!this.presentationsById[id]) throw new Error(`Could not initialize presentation with id '${id}'.`);

		await this.setPresentationById(id);

		// transform all slides within the presentation initially
		// To speed up initial presentation display we are not waiting for all transforms to complete. Instead, we trigger all transforms initially
		// and then wait during gotoSlide() whenever necessary.
		await this.transformSlideViews();

		await this.goToSlide(this.selectedSlideIds[0]);
	}

	@Action
	public async initNewPresentation(): Promise<void> {
		if (!appStore.isOnline) throw new OfflineError('Cannot create a new presentation while offline');

		if (!userStore.currentUser) {
			await userStore.loadCurrentUser();
		}
		if (globalsStore.contacts.length === 0) {
			await globalsStore.loadContacts();
		}

		// Make sure fresh master copy is loaded as base for the new presentation
		await this.loadCurrentPresentationMaster(process.env.PRESENTATION_MASTER_ID as string);
		await this.ensureSnapshotOfCurrentMasterExists(process.env.PRESENTATION_MASTER_ID as string);

		// Find a user entry(!) for the current user by email
		const primaryContact: IRawContact | undefined = globalsStore.contacts.find(
			(contact: IRawContact): boolean => contact.email === (userStore.currentUser as User).email
		);

		if (!primaryContact) {
			console.warn(`No Contact entry with email ${(userStore.currentUser as User).email} found. Please add one.`);
		}

		const master: IPresentationMaster = this.currentMastersByIdAndLocale[process.env.PRESENTATION_MASTER_ID as string][
			process.env.PRESENTATION_DEFAULT_LOCALE as string
		];

		const welcomeSlideId: string | undefined = master.slideIds.find(
			(slideId: string): boolean => master.slidesById[slideId].type === SlideType.WelcomeSlide
		);

		const welcomeSlideMaster: ISlideMaster | undefined = welcomeSlideId ? master.slidesById[welcomeSlideId] : undefined;

		const presentation: IPresentation = {
			id: '',
			masterId: master.id,
			masterChecksum: master.checksum,
			ownerId: (userStore.currentUser as User).sys.id, // Needs to be saved in presentation because "createdBy" is only accessible with management token
			checksum: '',
			customSlidesById: {},
			settings: {
				chapters: master.chapterIds.map((chapterId: string) => ({
					id: chapterId,
					showIntroVideo: true
				})),
				slides: [
					...master.slideIds.map(
						(slideId: string): ISlideSettings => ({
							id: slideId,
							selected: true
						})
					)
				],
				title: `Untitled – ${new Date().toISOString().split('T')[0]}`,
				locale: master.locale,
				clientLogo: '',
				showTravelGuideBenefits: false,
				systemOfUnits: SystemOfUnits.Metrical,
				welcomeSettings: {
					locale: master.locale,
					overline: welcomeSlideMaster ? welcomeSlideMaster.fields.overline : '',
					headline: welcomeSlideMaster ? welcomeSlideMaster.fields.headline : '',
					copy: welcomeSlideMaster ? welcomeSlideMaster.fields.copy : '',
					showPartnerLogos: false
				},
				primaryContactId: primaryContact ? primaryContact.id : '', // Needs to be saved in the settings because presentation's "createdBy" is only accessible with management token
				contactIds: [],
				staticUI: false
			},
			metadata: {
				selectedChapterTitles: master.chapterIds
					.map((chapterId: string): string => master.chaptersById[chapterId].title)
					.filter((chapterTitle: string): boolean => !!chapterTitle && chapterTitle.length > 0)
			}
		};
		const presentationService: PresentationService = new PresentationService();
		presentation.checksum = presentationService.getPresentationChecksum(presentation);

		this.registerPresentation(presentation);
		await this.setPresentationById('');
	}

	@Action
	public async initNewDuplicatedPresentation(sourcePresentationId: string): Promise<void> {
		if (!this.presentationsById[sourcePresentationId]) await this.loadPresentation(sourcePresentationId);

		await this.setPresentationById(sourcePresentationId);

		if (!userStore.currentUser) {
			await userStore.loadCurrentUser();

			userStore.saveStateIfSupported();
		}
		if (globalsStore.contacts.length === 0) {
			await globalsStore.loadContacts();

			globalsStore.saveStateIfSupported();
		}

		const duplicatedPresentation: IPresentation = JSON.parse(
			JSON.stringify(this.presentationsById[sourcePresentationId])
		) as IPresentation;

		// Find a user entry(!) for the current user by email
		const primaryContact: IRawContact | undefined = globalsStore.contacts.find(
			(contact: IRawContact): boolean => contact.email === (userStore.currentUser as User).email
		);

		if (!primaryContact) {
			console.warn(`No Contact entry with email ${(userStore.currentUser as User).email} found. Please add one.`);
		}

		duplicatedPresentation.id = '';
		duplicatedPresentation.ownerId = (userStore.currentUser as User).sys.id;
		duplicatedPresentation.settings.title = `(Duplicate) Untitled – ${new Date().toISOString().split('T')[0]}`;
		duplicatedPresentation.settings.primaryContactId = primaryContact ? primaryContact.id : ''; // Needs to be saved in the settings because presentation's "createdBy" is only accessible with management token

		this.registerPresentation(duplicatedPresentation);
		await this.initPresentationByIdAndStartTransforms('');
	}

	/**
	 * Transform all slides and store promises separately so we can
	 * wait for individual slides to be transformed
	 */
	@Action
	public async transformSlideViews(): Promise<void> {
		await this.loadPresentationMasterSnapshotIfNotAlreadyLoaded({
			masterId: this.presentation.masterId,
			checksum: this.presentation.masterChecksum
		});

		if (!this.master.id) {
			// the master for this presentation is not loaded, so we can't cache any assets
			console.warn(
				`[PresentationStoreModule] Cannot transform slides of presentation '${this.presentation.id}' as presentation master is unavailable.`
			);

			return;
		}

		const {slideViewPromisesById} = this;
		/* eslint-disable-next-line @typescript-eslint/ban-types */
		const resolveFunctionsBySlideId: Record<string, Function> = {};

		// if slideViewPromisesById is not empty, slide view transforms were already started
		if (Object.values(slideViewPromisesById).length > 0) return;

		// Set promises for every slide before the transforms start, so transforms can wait for each other
		this.slideIds.forEach((slideId: string): void => {
			/* eslint-disable-next-line @typescript-eslint/ban-types */
			slideViewPromisesById[slideId] = new Promise((resolve: Function): void => {
				resolveFunctionsBySlideId[slideId] = resolve;
			});
		});

		this.setSlideViewPromises(slideViewPromisesById);

		this.slideIds.forEach(
			async (slideId: string): Promise<void> => {
				// skip slides that do not exist anymore in master or custom slides
				if (!this.master.slidesById[slideId] && !this.presentation.customSlidesById[slideId]) return;

				const slideView: ISlideView = await transformSlideFunction(
					this.presentation.customSlidesById[slideId]
						? this.presentation.customSlidesById[slideId]
						: this.master.slidesById[slideId],
					this.settingsWithOverrides
				);
				// Make sure the slide view is set before the promise is resolved
				this.setSlideView(slideView);
				resolveFunctionsBySlideId[slideId](slideView);
			}
		);
	}

	@Action
	public async initializePresentationAssetCache(): Promise<void> {
		throwCacheErrorIfUnsupportedInClient();

		AssetCacheService.getInstance().reset();

		return this.startAssetCaching();
	}

	@Action
	public async resetAssetCache(): Promise<void> {
		throwCacheErrorIfUnsupportedInClient();

		const {presentationId} = this;
		this.setCachedPresentationsChecksums({
			[presentationId]: ''
		});

		await AssetCacheService.getInstance().reset();
	}

	@Action
	public async flushAssetCache(): Promise<void> {
		throwCacheErrorIfUnsupportedInClient();

		await AssetCacheService.getInstance().flush();
		return this.resetAssetCache();
	}

	@Action
	protected async startAssetCaching(): Promise<void> {
		throwCacheErrorIfUnsupportedInClient();

		const assetCacheService: AssetCacheService = AssetCacheService.getInstance();
		const checksum: string = this.presentation.checksum || '';
		const {presentationId} = this;

		await this.loadPresentationMasterSnapshotIfNotAlreadyLoaded({
			masterId: this.presentation.masterId,
			checksum: this.presentation.masterChecksum
		});

		if (!this.master.id) {
			// the master for this presentation is not loaded, so we can't cache any assets
			console.warn(
				`[PresentationStoreModule] Asset caching of presentation with id ${presentationId} skipped as presentation master is unavailable.`
			);
			this.setAssetCacheOffloads({
				[presentationId]: 0
			});

			return;
		}

		// build array of all asset urls to be cached
		const assetUrls: string[] = [];

		// No need to transform that were already transformed before with the same checksum.
		// As the transform is only used to generate a list of URLs, we will store this url list and simply reuse it
		// as long as the checksum remains the same
		if (this.cachedPresentationsChecksumsById[presentationId] === this.presentationsById[presentationId].checksum) {
			assetUrls.push(...Object.keys(this.assetLoadingPromisesByPresentationIdAndUrl[presentationId]));
		} else {
			// slides need to be transformed in order to extract and cache all assets that are used by the generated slide views
			await this.transformSlideViews();

			if (Object.values(this.slideViewsById).length === 0) {
				// no slides were transformed, cannot cache presentation
				this.setAssetCacheOffloads({
					[presentationId]: 0
				});

				return;
			}

			await Promise.all(Object.values(this.slideViewPromisesById));

			Object.values(this.slideViewsById).forEach((slideView: ISlideView): void => {
				assetUrls.push(...assetCacheService.extractAssetUrls(slideView.data));
			});
		}

		// initiate caching of asset urls
		const assetLoadingPromisesByUrl: Record<string, Promise<void>> = await assetCacheService.cacheAssets(assetUrls);

		// store loading promises so we can track loading progress
		this.setAssetLoadingPromises({
			[presentationId]: assetLoadingPromisesByUrl
		});

		// this is set to a value slightly bigger than zero to force a loading circle to be displayed initially instead of just the offline icon
		this.setAssetCacheOffloads({
			[presentationId]: 0.0001
		});

		// track cache progress and store it in the presentation list item
		let numPromisesResolved = 0;
		const numPromisesTotal = Object.values(assetLoadingPromisesByUrl).length;

		Object.values(assetLoadingPromisesByUrl).forEach(
			async (promise: Promise<void>): Promise<void> => {
				try {
					await promise;
					numPromisesResolved++;
					// store loading promises so we can track loading progress
					this.setAssetCacheOffloads({
						[presentationId]: numPromisesResolved / numPromisesTotal
					});
				} catch (error) {
					// noop - error will be handled for Promise.all()
				}
			}
		);

		Promise.all(Object.values(assetLoadingPromisesByUrl))
			.then((): void => {
				if (this.cachedPresentationsChecksumsById[presentationId] !== checksum) {
					// Once all assets are cached mark the current presentation checksum as being the latest cached revision of this presentation
					this.setCachedPresentationsChecksums({
						[presentationId]: checksum
					});

					console.debug(`[PresentationStoreModule] Asset caching of presentation id ${presentationId} completed.`);
				}
			})
			.catch((): void => {
				// if even a single asset could not be fetched the presenation will not be presentable, hence mark it as unavailable
				this.setAssetCacheOffloads({
					[presentationId]: 0
				});
				console.warn(
					`[PresentationStoreModule] Asset caching of presentation with id ${presentationId} could not be completed due to errors during fetch.`
				);
			});
	}

	@Action
	public updateSettings(settings: IPresentationSettings): void {
		this.setSettings(settings);
	}

	/**
	 * Duplicates a custom slide, replacing it in both, the customSlidesById dict and the presentation's slide settings
	 */
	@Action
	private async duplicateCustomSlide(oldCustomSlide: ICustomSlide): Promise<ICustomSlide> {
		const customSlideService: CustomSlideService = new CustomSlideService();

		try {
			const newCustomSlide: ICustomSlide = await customSlideService.create(oldCustomSlide);

			// replace old custom slide id in slideSettings
			this.presentation.settings.slides = this.presentation.settings.slides.map(
				(slideSettings: ISlideSettings): ISlideSettings => {
					if (slideSettings.id === oldCustomSlide.id)
						return {
							...slideSettings,
							id: newCustomSlide.id
						};

					return slideSettings;
				}
			);

			delete this.presentation.customSlidesById[oldCustomSlide.id];
			this.presentation.customSlidesById[newCustomSlide.id] = newCustomSlide;

			return newCustomSlide;
		} catch (error) {
			if (isNetworkOrOfflineErrorAndAppIsOffline(error)) {
				// application went offline during network requests
				throw new OfflineError('Cannot save presentation - application is offline');
			} else {
				throw error;
			}
		}
	}

	@Action
	public async savePresentation(): Promise<void> {
		const presentationService: PresentationService = new PresentationService();

		const presentationBeforeAction: IPresentation = JSON.parse(JSON.stringify(this.presentation));

		try {
			if (!appStore.isOnline) throw new OfflineError();

			if (!userStore.currentUser) throw new AuthenticationError('Cannot save presentation as no user is logged in');
			if (globalsStore.contacts.length === 0) await globalsStore.loadContacts();

			// update primary contact in case it is not already set
			// this ensures contact data is added in case no contact entry for the current user was present by the time the presentation was created
			if (this.presentation.settings.primaryContactId === '') {
				// Find a user entry(!) for the current user by email
				const primaryContact: IRawContact | undefined = globalsStore.contacts.find(
					(contact: IRawContact): boolean => contact.email === (userStore.currentUser as User).email
				);

				if (primaryContact) {
					this.presentation.settings.primaryContactId = primaryContact.id; // Needs to be saved in the settings because presentation's "createdBy" is only accessible with management token
					this.registerPresentation(this.presentation);
				} else {
					console.warn(
						`No Contact entry with email ${
							(userStore.currentUser as User).email
						} found - primary contact cannot be identified. Please add one.`
					);
				}
			}

			// remove duplicate slide settings (that may have been introduced if a slide was linked more than once - even if only for a short time)
			this.presentation.settings.slides = this.presentation.settings.slides.filter(
				(slideSettings: ISlideSettings, index: number, slides: ISlideSettings[]): boolean =>
					index ===
					slides.findIndex((findSlideSettings: ISlideSettings): boolean => findSlideSettings.id === slideSettings.id)
			);

			// remove duplicate chapter settings (that may have been introduced if a slide was linked more than once - even if only for a short time)
			this.presentation.settings.chapters = this.presentation.settings.chapters.filter(
				(chapterSettings: IChapterSettings, index: number, chapters: IChapterSettings[]): boolean =>
					index ===
					chapters.findIndex(
						(findChapterSettings: IChapterSettings): boolean => findChapterSettings.id === chapterSettings.id
					)
			);

			this.registerPresentation({
				...this.presentation,
				metadata: {
					...this.presentation.metadata,
					selectedChapterTitles: this.selectedChapterIds
						.map((chapterId: string): string => this.master.chaptersById[chapterId].title)
						.filter((chapterTitle: string): boolean => !!chapterTitle && chapterTitle.length > 0)
				}
			});

			if (this.presentation.id) {

				const masterSnapShot: IPresentationMaster | undefined = PresentationMasterUtil.findMasterSnapshotByIdChecksumAndLocale(
					this.presentation,
					this.masterSnapshotsByMasterIdChecksumAndLocale
				);

				if (!masterSnapShot) {
					throw new InvalidMasterSnapshotError('Trying to save presentation with unknown master snapshot');
				}

				await presentationService.update(this.presentation, masterSnapShot);

				return;
			}

			// New presentations can never have custom slides yet, unless the presentation has been duplicated.
			// Those need to be individually duplicated (they would be share across presentations otherwise)
			// and slide settings need to be adjusted accordingl to match the new custom slide id
			const customSlidesToDuplicate: ICustomSlide[] = Object.values(this.presentation.customSlidesById);

			await Promise.all(customSlidesToDuplicate.map(this.duplicateCustomSlide));

			const presentation: IPresentation = await presentationService.create(
				this.presentation,
				this.masterSnapshotsByMasterIdChecksumAndLocale[this.presentation.masterId][this.presentation.masterChecksum][
					this.presentation.settings.locale
				]
			);

			this.registerPresentation(presentation);
			await this.setPresentationById(presentation.id);
		} catch (error) {
			// revert changes to store before forwarding error
			this.registerPresentation(presentationBeforeAction);

			if (isNetworkOrOfflineErrorAndAppIsOffline(error)) {
				// application went offline during network requests
				throw new OfflineError('Cannot create presentation - application is offline');
			} else {
				throw error;
			}
		}
	}

	@Action
	public async saveCustomSlide(customSlide: ICustomSlide): Promise<ICustomSlide> {
		const presentationService: PresentationService = new PresentationService();
		const customSlideService: CustomSlideService = new CustomSlideService();

		try {
			if (!appStore.isOnline) throw new OfflineError();

			// If the custom slide does have an id yet, but the presentation id is empty, it originates from a different presentation.
			// Then only the custom slides data needs to be updated, real duplication takes place in `savePresentation()`
			if (customSlide.id && !customSlide.presentationId) {
				Vue.set(this.presentationsById[this.presentationId as string].customSlidesById, customSlide.id, customSlide);
				await this.savePresentation();
				return customSlide;
			}

			const processedCustomSlide: ICustomSlide = customSlide.id
				? await customSlideService.update(customSlide)
				: await customSlideService.create(customSlide);

			if (!customSlide.presentationId && this.presentation.id) {
				// eslint-disable-next-line no-param-reassign
				customSlide.presentationId = this.presentationId;
			} else if (!customSlide.presentationId) {
				await this.savePresentation();
				// eslint-disable-next-line no-param-reassign
				customSlide.presentationId = this.presentationId;
			}

			// update slide in customSlidesById
			Vue.set(
				this.presentationsById[customSlide.presentationId as string].customSlidesById,
				processedCustomSlide.id,
				processedCustomSlide
			);

			const presentationToUpdate: IPresentation = { ...this.presentationsById[customSlide.presentationId] };

			// add slide settings if they do not already exist
			if (
				!presentationToUpdate.settings.slides.find(
					(slideSettings: ISlideSettings): boolean => slideSettings.id === processedCustomSlide.id
				)
			) {
				presentationToUpdate.settings.slides.push({
					id: processedCustomSlide.id,
					selected: true
				});

				// if a new custom slide was created we need to update custom slides references in the presentation entry
				// please note: this will save the entire presentation, not only custom slide settings
				await presentationService.update(presentationToUpdate);

				// update presentation in store only after successfully updating presentation via service
				Vue.set(this.presentationsById, presentationToUpdate.id, presentationToUpdate);
			}

			// save presentation reference in updated custom slide master
			processedCustomSlide.presentationId = customSlide.presentationId;

			// re-register presentation to ensure its settings are up to date and checksum is recalculated
			this.registerPresentation(this.presentationsById[customSlide.presentationId]);

			return processedCustomSlide;
		} catch (error) {
			if (isNetworkOrOfflineErrorAndAppIsOffline(error)) {
				// application went offline during network requests
				throw new OfflineError('Cannot save custom slide - application is offline');
			}
			throw error;
		}
	}

	@Action
	public async removeCustomSlide(customSlide: ICustomSlide): Promise<void> {
		let updatedPresentation: IPresentation | undefined;

		try {
			if (!appStore.isOnline) throw new OfflineError();

			// remove custom slide from customSlidesById as well as slides settings
			if (customSlide.presentationId && this.presentationsById[customSlide.presentationId]) {
				updatedPresentation = { ...this.presentationsById[customSlide.presentationId] };

				delete updatedPresentation.customSlidesById[customSlide.id];

				updatedPresentation.settings.slides = updatedPresentation.settings.slides.filter(
					(slideSettings: ISlideSettings): boolean => slideSettings.id !== customSlide.id
				);

				// update the custom slides presentation to not include the slide any longer
				// this will save the entire presentation, not only custom slide settings
				const presentationService: PresentationService = new PresentationService();
				await presentationService.update(this.presentationsById[customSlide.presentationId]);

				// eventually, after saving succeeded:
				Vue.set(this.presentationsById, customSlide.presentationId, updatedPresentation);
			}

			const customSlideService: CustomSlideService = new CustomSlideService();
			await customSlideService.delete(customSlide);
		} catch (error) {
			if (isNetworkOrOfflineErrorAndAppIsOffline(error)) {
				// application went offline during network requests
				throw new OfflineError('Cannot complete custom slide removal - application is offline');
			} else {
				throw error;
			}
		}
	}

	/**
	 * Sets the function to transform slides.
	 **
	 * @param transformFunction
	 */
	@Action
	public setSlideTransformFunction(transformFunction: SlideTransform): void {
		transformSlideFunction = transformFunction;
	}

	@Action
	public async deletePresentationById(id: string): Promise<void> {
		const presentationService: PresentationService = new PresentationService();
		const customSlideService: CustomSlideService = new CustomSlideService();

		const presentation: IPresentation = JSON.parse(JSON.stringify(this.presentationsById[id]));

		try {
			if (!appStore.isOnline) throw new OfflineError();

			await presentationService.delete(id);

			this.removePresentationById(id);

			if (this.presentationId === id) {
				this.setPresentationById('');
			}
		} catch (error) {
			if (isNetworkOrOfflineErrorAndAppIsOffline(error)) {
				// application went offline during network requests
				this.registerPresentation(presentation);
				throw new OfflineError('Cannot delete a presentation while offline');
			}
			throw error;
		}
		try {
			Object.values(presentation.customSlidesById).forEach((customSlide: ICustomSlide): void => {
				customSlideService.delete(customSlide);
			});
		} catch (error) {
			if (isNetworkOrOfflineErrorAndAppIsOffline(error)) {
				// application went offline during network requests
				throw new OfflineError('Some custom slides could not be deleted as the application is offline');
			}
		}
	}

	@Action
	public setPresentationById(id: string): void {
		this.setPresentationId(id);

		appStore.setLocale(this.settingsWithOverrides.locale);
		appStore.setSystemOfUnits(this.settingsWithOverrides.systemOfUnits);

		this.resetSlideViews();
	}

	@Action
	public async initializeUserPresentationsAssetCache(): Promise<void> {
		throwCacheErrorIfUnsupportedInClient();
		//  The cache may have been cleared since the last call to `initializeUserPresentationAssetCache()`
		// Make sure the cace services validates cache items anew instead of relying on previous tests and reusing previously created fetch-promises
		// Note that this is not a cache flush - just a reset on the cache tests performed
		await AssetCacheService.getInstance().reset();
		// eslint-disable-next-line no-restricted-syntax
		for (const presentation of this.currentUserPresentations) {
			this.setPresentationById(presentation.id);
			// eslint-disable-next-line no-await-in-loop
			await this.startAssetCaching();
		}
	}

	@Action
	public async upgradePresentationById(id: string | string[]): Promise<void> {
		try {
			if (!appStore.isOnline) throw new OfflineError();

			const presentationIdsToUpgrade: string[] = Array.isArray(id) ? id : [id];

			// fetch fresh copies of all presentations to upgrade from contentful
			await Promise.all(
				presentationIdsToUpgrade
					.filter((presentationId: string): boolean => !this.presentationsById[presentationId])
					.map(
						(presentationId: string): Promise<void> =>
							this.loadPresentation(presentationId).catch(throwIfNotOfflineError)
					)
			);

			// ensure current masters used in presentations to upgrade are fetched from contentful and a master snapshot exists
			await Promise.all(
				presentationIdsToUpgrade
					.map((presentationId: string): string => this.presentationsById[presentationId].masterId)
					.filter((masterId: string, index: number, self: string[]): boolean => self.indexOf(masterId) === index)
					.map(
						async (masterId: string): Promise<any> => {
							await this.loadCurrentPresentationMaster(masterId);
							await this.ensureSnapshotOfCurrentMasterExists(masterId);
						}
					)
			);

			// build list of presentations to upgrade from presentation id list
			const presentationsToUpgrade: IPresentation[] = presentationIdsToUpgrade.map(
				(presentationId: string): IPresentation => ({ ...this.presentationsById[presentationId] })
			);

			await AssetCacheService.getInstance().reset();

			// eslint-disable-next-line no-restricted-syntax
			for (const presentation of presentationsToUpgrade) {
				await this.setPresentationById(presentation.id); // eslint-disable-line no-await-in-loop

				const newMasterChecksum: string = this.currentMastersByIdAndLocale[presentation.masterId][
					presentation.settings.locale
				].checksum;

				console.log(
					`[PresentationStoreModule] Upgrading presentation ${presentation.id} with master ${presentation.masterId}/${presentation.masterChecksum} to /${newMasterChecksum}`
				);
				const oldMasterChecksum: string = presentation.masterChecksum;
				const oldPresentationChecksum: string = presentation.checksum;

				try {
					presentation.masterChecksum = newMasterChecksum;

					const presentationService: PresentationService = new PresentationService();
					presentation.checksum = presentationService.getPresentationChecksum(presentation);

					this.registerPresentation(presentation);

					await this.savePresentation(); // eslint-disable-line no-await-in-loop
				} catch (error) {
					if (isNetworkOrOfflineErrorAndAppIsOffline(error)) {
						// application went offline during network requests
						// revert checksum changes to presentation as they could not be saved
						presentation.masterChecksum = oldMasterChecksum;
						presentation.checksum = oldPresentationChecksum;
						this.registerPresentation(presentation);
					} else {
						throwIfNotCachingUnsupportedInClient(error);
					}

					presentation.masterChecksum = oldMasterChecksum;
					this.registerPresentation(presentation);
					break;
				}
			}
		} catch (error) {
			if (isNetworkOrOfflineErrorAndAppIsOffline(error)) {
				throw new OfflineError('Cannot upgrade presentations while offline');
			}
			throw error;
		}
	}

	@Action
	public async exportPowerPointPresentation(): Promise<void> {
		await this.transformSlideViews();
		await Promise.all(Object.values(this.slideViewPromisesById));

		const selectedSlideViews: ISlideView[] = this.selectedSlideIds.map(
			(slideId): ISlideView => this.slideViewsById[slideId]
		);

		const service: PowerPointExportService = new PowerPointExportService();

		return service.createPresentation(
			selectedSlideViews,
			this.presentation.settings.title,
			userStore.currentUser ? `${userStore.currentUser.firstName} ${userStore.currentUser.lastName}` : undefined
		);
	}

	@Action
	protected async registerPresentationIfMasterExists(presentation: IPresentation): Promise<void> {
		await this.loadPresentationMasterSnapshotIfNotAlreadyLoaded({
			masterId: presentation.masterId,
			checksum: presentation.masterChecksum
		});

		if (
			!!this.masterSnapshotsByMasterIdChecksumAndLocale[presentation.masterId] &&
			!!this.masterSnapshotsByMasterIdChecksumAndLocale[presentation.masterId][presentation.masterChecksum] &&
			!!this.masterSnapshotsByMasterIdChecksumAndLocale[presentation.masterId][presentation.masterChecksum][
				presentation.settings.locale
			]
		) {
			this.registerPresentation(presentation);
		}
	}

	@Mutation
	protected removePresentationById(id: string): void {
		Vue.delete(this.presentationsById, id);
	}

	@Mutation
	public setPresentationId(id: string): void {
		this.presentationId = id;
	}

	@Mutation
	public setOverridePresentationSettings(overridePresentationSettings: Partial<IPresentationSettings>): void {
		Vue.set(this, 'overridePresentationSettings', JSON.parse(JSON.stringify(overridePresentationSettings)));
	}

	@Mutation
	protected registerPresentation(presentation: IPresentation): void {
		if (
			!this.masterSnapshotsByMasterIdChecksumAndLocale[presentation.masterId] ||
			!this.masterSnapshotsByMasterIdChecksumAndLocale[presentation.masterId][presentation.masterChecksum] ||
			!this.masterSnapshotsByMasterIdChecksumAndLocale[presentation.masterId][presentation.masterChecksum][
				presentation.settings.locale
			]
		) {
			throw new Error(
				`Cannot register presentation '${presentation.id}' with non-loaded master snapshot ${presentation.masterId}.${presentation.masterChecksum}`
			);
		}

		const master: IPresentationMaster = this.masterSnapshotsByMasterIdChecksumAndLocale[presentation.masterId][
			presentation.masterChecksum
		][presentation.settings.locale];

		const customSlideIds: string[] = presentation.customSlidesById ? Object.keys(presentation.customSlidesById) : [];

		// add new slides which do not have slide settings yet and deselect expired ones
		[...master.slideIds, ...customSlideIds].forEach((slideId: string): void => {
			const slideMaster: ISlideMaster | undefined = master.slidesById[slideId];

			const slideSettings: ISlideSettings | undefined = presentation.settings.slides.find(
				(slideSettingItem: ISlideSettings): boolean => slideSettingItem.id === slideId
			);

			if (!slideSettings) {
				const newSlideSettings: ISlideSettings = {
					id: slideId,
					selected:
						slideMaster &&
						[SlideType.WelcomeSlide, SlideType.ThankYouSlide, SlideType.ChapterOverviewSlide].includes(slideMaster.type) // these slide types cannot be de-selected
				};
				// welcome slide and especially chapter overview slides need ot be inserted at the beginning,
				// otherwise they will be displayed at the end of their respective chapter
				if (slideMaster && [SlideType.WelcomeSlide, SlideType.ChapterOverviewSlide].includes(slideMaster.type)) {
					presentation.settings.slides.unshift(newSlideSettings);
				} else {
					presentation.settings.slides.push(newSlideSettings);
				}
			}
		});
		// Chapter Overviews and Welcome Slides may have been wrongfully put amidst other slides instead of the beginning of their chapter
		// --> sort slide settings and ensure Chapter Overviews and Welcome Slides are shifted to the beginning of the slide settings (^= beginning of their respective chapter):
		// eslint-disable-next-line no-param-reassign
		presentation.settings.slides = presentation.settings.slides.sort(
			(slideSettingItemA: ISlideSettings, slideSettingItemB: ISlideSettings): number => {
				const slideMasterA: ISlideMaster | undefined = master.slidesById[slideSettingItemA.id];
				if (slideMasterA && [SlideType.WelcomeSlide, SlideType.ChapterOverviewSlide].includes(slideMasterA.type))
					return -1;

				const slideMasterB: ISlideMaster | undefined = master.slidesById[slideSettingItemB.id];
				if (slideMasterB && [SlideType.WelcomeSlide, SlideType.ChapterOverviewSlide].includes(slideMasterB.type))
					return 1;

				return 0;
			}
		);

		// remove slides that do not exist any longer in master or custom slides
		// eslint-disable-next-line no-param-reassign
		presentation.settings.slides = presentation.settings.slides.filter(
			(slideSettings: ISlideSettings): boolean =>
				master.slideIds.includes(slideSettings.id) || customSlideIds.includes(slideSettings.id)
		);

		// add new chapter settings that do not exist yet
		master.chapterIds.forEach((chapterId: string): void => {
			if (
				!presentation.settings.chapters.some((chapterSettings: IChapterSettings) => chapterSettings.id === chapterId)
			) {
				presentation.settings.chapters.splice(-1, 0, { id: chapterId, showIntroVideo: true });
			}
		});

		// remove chapter settings that do not exist any longer
		// eslint-disable-next-line no-param-reassign
		presentation.settings.chapters = presentation.settings.chapters.filter(
			(chapterSettings: IChapterSettings): boolean => master.chapterIds.includes(chapterSettings.id)
		);

		Vue.set(this.presentationsById, presentation.id, presentation);

		if (!this.assetLoadingPromisesByPresentationIdAndUrl[presentation.id])
			Vue.set(this.assetLoadingPromisesByPresentationIdAndUrl, presentation.id, {});

		if (!this.assetCacheOffloadsByPresentationId[presentation.id])
			Vue.set(this.assetCacheOffloadsByPresentationId, presentation.id, 0);
	}

	@Mutation
	public setCachedPresentationsChecksums(cachedPresentationsChecksumsById: Record<string, string>): void {
		Object.keys(cachedPresentationsChecksumsById).forEach((key: string): void => {
			Vue.set(this.cachedPresentationsChecksumsById, key, cachedPresentationsChecksumsById[key]);
		});
	}

	@Mutation
	public resetCachedPresentationsChecksums(): void {
		Vue.set(this, 'cachedPresentationsChecksumsById', {});
	}

	@Mutation
	public setAssetLoadingPromises(
		assetLoadingPromisesByPresentationIdAndUrl: Record<string, Record<string, Promise<void>>>
	): void {
		Object.keys(assetLoadingPromisesByPresentationIdAndUrl).forEach((key: string): void => {
			Vue.set(this.assetLoadingPromisesByPresentationIdAndUrl, key, assetLoadingPromisesByPresentationIdAndUrl[key]);
		});
	}

	@Mutation
	public setAssetCacheOffloads(assetCacheOffloadsByPresentationId: Record<string, number>): void {
		Object.keys(assetCacheOffloadsByPresentationId).forEach((key: string): void => {
			Vue.set(this.assetCacheOffloadsByPresentationId, key, assetCacheOffloadsByPresentationId[key]);
		});
	}

	@Mutation
	protected setSettings(settings: IPresentationSettings): void {
		//	Vue.set(this.presentationsById[this.presentationId], 'settings', settings);

		this.presentationsById[this.presentationId].settings = settings;
	}

	@Mutation
	protected setPresentationMasterSnapshot(master: IPresentationMaster): void {
		if (!this.masterSnapshotsByMasterIdChecksumAndLocale[master.id])
			Vue.set(this.masterSnapshotsByMasterIdChecksumAndLocale, master.id, {});
		if (!this.masterSnapshotsByMasterIdChecksumAndLocale[master.id][master.checksum])
			Vue.set(this.masterSnapshotsByMasterIdChecksumAndLocale[master.id], master.checksum, {});
		Vue.set(this.masterSnapshotsByMasterIdChecksumAndLocale[master.id][master.checksum], master.locale, master);
	}

	@Mutation
	protected setCurrentPresentationMaster(master: IPresentationMaster): void {
		if (!this.currentMastersByIdAndLocale[master.id]) Vue.set(this.currentMastersByIdAndLocale, master.id, {});
		Vue.set(this.currentMastersByIdAndLocale[master.id], master.locale, master);
	}

	/**
	 * Sets id of the current slide. This will affect
	 * a) get currentSlide
	 * b) the action taken when goToNextSlide() / goToPreviousSlide() are invoked
	 */
	@Mutation
	protected setCurrentSlideId(slideId: string): void {
		if (!this.slideViewPromisesById[slideId]) throw new Error(`Unknown Slide id: ${slideId}`);
		this.currentSlideId = slideId;
	}

	/**
	 * Store a single slideView once transformed
	 * @param slideView
	 */
	@Mutation
	protected setSlideView(slideView: ISlideView): void {
		Vue.set(this.slideViewsById, slideView.data.id, slideView);
	}

	/**
	 * Reset existing slide views
	 */
	@Mutation
	protected resetSlideViews(): void {
		Object.keys(this.slideViewsById).forEach((key: string): void => Vue.delete(this.slideViewsById, key));

		Object.keys(this.slideViewPromisesById).forEach((key: string): void => Vue.delete(this.slideViewPromisesById, key));
	}

	/**
	 * Store an slideId => Promise map of all slideView transformation promises so
	 * we can wait for individual slides to be transformed as soon as we know the slide id
	 *
	 * @param slideViewPromisesById
	 */
	@Mutation
	protected setSlideViewPromises(slideViewPromisesById: Record<string, Promise<ISlideView>>): void {
		this.slideViewPromisesById = slideViewPromisesById;
	}

	/**
	 * Chapter ids of chapters with in order
	 */
	public get slideSettingsById(): Record<string, ISlideSettings> {
		const slideSettingsById: Record<string, ISlideSettings> = {};

		this.presentation.settings.slides.forEach((slide: ISlideSettings): void => {
			slideSettingsById[slide.id] = slide;
		});

		return slideSettingsById;
	}

	/**
	 * Chapter ids of chapters with in order
	 */
	public get chapterIds(): string[] {
		return this.presentation.settings.chapters.map((chapterSettings: IChapterSettings) => chapterSettings.id);
	}

	/**
	 * Dictionary of chapters that contain selected chapter items, items ordered as configured in the current presentation
	 */
	public get chapterItemsByChapterId(): Record<string, IChapterItem[]> {
		// find index of a slide in the selectedSlideIds array
		const findSlideOrderIndex = (slideId: string): number =>
			this.slideIds.findIndex((selectedSlideId: string): boolean => selectedSlideId === slideId);

		// find lowest slide index in the selectedSlideIds array for a section
		const findMinSelectedSlideIndexForSection = (sectionId: string): number =>
			Math.min(
				...this.master.sectionsById[sectionId].slides
					.map((slideId: string): number => findSlideOrderIndex(slideId))
					.filter((slideIndex: number): boolean => slideIndex >= 0)
			);

		const chapterItemsByChapterId: Record<string, IChapterItem[]> = {};

		this.presentation.settings.chapters.forEach((chapterSettings: IChapterSettings): void => {
			const joinedChapterItems: IChapterItem[] = ([] as IChapterItem[]).concat(
				...this.master.chaptersById[chapterSettings.id].items,
				...Object.values(this.presentation.customSlidesById)
					.filter((customSlide: ICustomSlide): boolean => customSlide.chapterId === chapterSettings.id)
					.map(
						(customSlide: ICustomSlide): IChapterItem => ({
							id: customSlide.id,
							type: ChapterItemType.Slide
						})
					)
			);

			chapterItemsByChapterId[chapterSettings.id] = joinedChapterItems
				// sort slides and sections in the chapter item based on the order they appear in the current presentation
				.sort((itemA: IChapterItem, itemB: IChapterItem): number => {
					// determine index of
					const sortOrderItemA: number =
						itemA.type === ChapterItemType.Section
							? findMinSelectedSlideIndexForSection(itemA.id)
							: findSlideOrderIndex(itemA.id);

					const sortOrderItemB: number =
						itemB.type === ChapterItemType.Section
							? findMinSelectedSlideIndexForSection(itemB.id)
							: findSlideOrderIndex(itemB.id);

					return sortOrderItemA - sortOrderItemB;
				});
		});

		return chapterItemsByChapterId;
	}

	/**
	 * *All* slide ids of the presentation, in order (*including non-selected ones*)
	 */
	public get slideIds(): string[] {
		const slideIds: string[] = [];

		this.presentation.settings.chapters.forEach((chapterSettings: IChapterSettings): void => {
			if (typeof chapterSettings === 'string') {
				// eslint-disable-next-line no-param-reassign
				chapterSettings = {
					id: chapterSettings,
					showIntroVideo: true
				};
			}

			slideIds.push(...this.slideIdsByChapterId[chapterSettings.id]);
		});

		// Warn for duplicate slides
		slideIds
			.filter((slideId: string, index: number): boolean => slideIds.indexOf(slideId) !== index)
			.forEach((slideId: string): void => {
				console.warn(
					`Duplicate slide in presentation master ${this.master.id} with checksum ${this.master.checksum}: https://app.contentful.com/spaces/${process.env.CONTENTFUL_SPACE_ID}/entries/${slideId}.`
				); // eslint-disable-line no-console
			});

		// only include the first occurence of a slide
		return slideIds.filter((slideId: string, index: number): boolean => slideIds.indexOf(slideId) === index);
	}

	/**
	 * *All* slide ids in each chapter, in the order (*including non-selected ones*)
	 */
	public get slideIdsByChapterId(): Record<string, string[]> {
		const slideIdsByChapterId: Record<string, string[]> = {};

		const slideOrder: Record<string, number> = {};
		const presentationSettingsSlideIds: string[] = this.presentation.settings.slides.map(
			(slideSettings: ISlideSettings): string => slideSettings.id
		);

		presentationSettingsSlideIds.forEach((slideId: string, index: number): void => {
			slideOrder[slideId] = index;
		});

		this.master.chapterIds.forEach((chapterId: string): void => {
			slideIdsByChapterId[chapterId] = [];

			const chapterMaster: IChapterMaster = this.master.chaptersById[chapterId];

			const recursiveChapterItemIds: string[] = [];

			chapterMaster.items.forEach((item: IChapterItem): void => {
				if (item.type === ChapterItemType.Section) {
					recursiveChapterItemIds.push(...this.master.sectionsById[item.id].slides);
				} else {
					recursiveChapterItemIds.push(item.id);
				}
			});

			// inject custom slides
			if (this.presentation.customSlidesById) {
				Object.values(this.presentation.customSlidesById).forEach((customSlideEntry: ICustomSlide): void => {
					if (customSlideEntry.chapterId === chapterId) {
						recursiveChapterItemIds.push(customSlideEntry.id);
					}
				});
			}

			slideIdsByChapterId[chapterId].push(
				...presentationSettingsSlideIds
					.filter((slideId: string): boolean => recursiveChapterItemIds.includes(slideId))
					// filter slides the primary contact is not allowed to access
					.filter((slideId: string): boolean => {
						const slideMaster: ISlideMaster =
							this.master.slidesById[slideId] || this.presentation.customSlidesById[slideId];

						return MarketRestrictionUtil.contactSatisfiesMarketRestrictions(
							this.primaryContact,
							slideMaster.fields.marketRestriction
						);
					})
					.sort((itemA: string, itemB: string): number => slideOrder[itemA] - slideOrder[itemB])
			);
		});

		return slideIdsByChapterId;
	}

	/**
	 * Dictionary of slide ids by section ids, in order, *including non-selected ones*
	 */
	public get slideIdsBySectionId(): Record<string, string[]> {
		const slideIdsBySectionId: Record<string, string[]> = {};

		Object.keys(this.master.sectionsById).forEach((sectionId: string): void => {
			slideIdsBySectionId[sectionId] = this.slideIds.filter((slideId: string): boolean =>
				this.master.sectionsById[sectionId].slides.includes(slideId)
			);
		});

		return slideIdsBySectionId;
	}

	/**
	 * Chapter ids of chapters with selected slides in order
	 */
	public get selectedChapterIds(): string[] {
		return this.chapterIds.filter(
			(chapterId: string): boolean =>
				this.selectedSlideIdsByChapterId[chapterId] && this.selectedSlideIdsByChapterId[chapterId].length > 0
		);
	}

	/**
	 * Dictionary of chapters that contain selected chapter items, items ordered as configured in the current presentation
	 */
	public get selectedChapterItemsByChapterId(): Record<string, IChapterItem[]> {
		const selectedChapterItemsByChapterId: Record<string, IChapterItem[]> = {};

		Object.keys(this.chapterItemsByChapterId).forEach((chapterId: string): void => {
			selectedChapterItemsByChapterId[chapterId] = this.chapterItemsByChapterId[chapterId].filter(
				(chapterItem: IChapterItem): boolean =>
					this.selectedSlideIds.includes(chapterItem.id) ||
					(this.selectedSlideIdsBySectionId[chapterItem.id] &&
						this.selectedSlideIdsBySectionId[chapterItem.id].length > 0)
			);
		});

		return selectedChapterItemsByChapterId;
	}

	/**
	 * Primary contact of the current presentation
	 */
	public get primaryContact(): IRawContact | undefined {
		// save loop if no primary contact is set
		if (!this.presentation.settings.primaryContactId) {
			return undefined;
		}

		return globalsStore.contacts.find(
			(contact: IRawContact): boolean => contact.id === this.presentation.settings.primaryContactId
		);
	}

	/**
	 * *Selected* slide ids in the order of the current presentation (*not including non-selected ones*)
	 */
	public get selectedSlideIds(): string[] {
		const slideIds: string[] = [];

		this.selectedChapterIds.forEach((chapterId: string): void => {
			slideIds.push(...this.selectedSlideIdsByChapterId[chapterId]);
		});

		// only include the first occurence of a slide
		return slideIds.filter((slideId: string, index: number): boolean => slideIds.indexOf(slideId) === index);
	}

	/**
	 * *All* slide ids in each chapter, in the order (*including non-selected ones*)
	 */
	public get selectedSlideIdsByChapterId(): Record<string, string[]> {
		const selectedSlideIdsByChapterId: Record<string, string[]> = {};

		Object.keys(this.slideIdsByChapterId).forEach((chapterId: string): void => {
			const selectedChapterSlideIds: string[] = this.slideIdsByChapterId[chapterId].filter(
				(slideId: string): boolean => !this.slideSettingsById[slideId] || this.slideSettingsById[slideId].selected
			);

			if (
				selectedChapterSlideIds.length > 1 ||
				(selectedChapterSlideIds.length === 1 &&
					(!this.master.slidesById[selectedChapterSlideIds[0]] || // Custom slide
						this.master.slidesById[selectedChapterSlideIds[0]].type !== SlideType.ChapterOverviewSlide))
			) {
				selectedSlideIdsByChapterId[chapterId] = selectedChapterSlideIds;
			}
		});

		return selectedSlideIdsByChapterId;
	}

	/**
	 * Dictionary of *selected* slide ids by section ids, in order
	 */
	public get selectedSlideIdsBySectionId(): Record<string, string[]> {
		const selectedSlideIdsBySectionId: Record<string, string[]> = {};

		Object.keys(this.slideIdsBySectionId).forEach((sectionId: string): void => {
			selectedSlideIdsBySectionId[sectionId] = this.slideIdsBySectionId[sectionId].filter((slideId: string): boolean =>
				this.selectedSlideIds.includes(slideId)
			);
		});
		return selectedSlideIdsBySectionId;
	}

	/**
	 * Transformed ISlideView object matching the current slide
	 */
	public get currentSlideView(): ISlideView | null {
		return this.currentSlideId ? this.slideViewsById[this.currentSlideId] : null;
	}

	/**
	 * Current IPresentationMaster
	 */
	public get master(): IPresentationMaster {
		if (
			this.settingsWithOverrides &&
			this.settingsWithOverrides.locale &&
			this.masterSnapshotsByMasterIdChecksumAndLocale[this.presentation.masterId] &&
			this.masterSnapshotsByMasterIdChecksumAndLocale[this.presentation.masterId][this.presentation.masterChecksum] &&
			this.masterSnapshotsByMasterIdChecksumAndLocale[this.presentation.masterId][this.presentation.masterChecksum][
				this.settingsWithOverrides.locale
			]
		) {
			return this.masterSnapshotsByMasterIdChecksumAndLocale[this.presentation.masterId][
				this.presentation.masterChecksum
			][this.settingsWithOverrides.locale];
		}

		return {
			id: '',
			checksum: '',
			locale: process.env.PRESENTATION_DEFAULT_LOCALE as string,
			chapterIds: [],
			chaptersById: {},
			sectionsById: {},
			slidesById: {},
			slideIds: [],
			changelog: [],
			dateCreated: 0
		};
	}

	/**
	 * Current IPresentationMaster
	 */
	public get presentation(): IPresentation {
		return (
			this.presentationsById[this.presentationId] || {
				id: '',
				locale: '',
				masterId: '',
				customSlidesById: {},
				ownerId: '',
				settings: {
					chapters: [],
					slides: [],
					title: 'Untitled',
					locale: process.env.PRESENTATION_DEFAULT_LOCALE as string,
					clientLogo: '',
					showTravelGuideBenefits: false,
					systemOfUnits: SystemOfUnits.Metrical,
					staticUI: false,
					welcomeSettings: {
						overline: '',
						headline: '',
						copy: '',
						showPartnerLogos: false
					},
					primaryContactId: '',
					contactIds: []
				}
			}
		);
	}

	public get presentations(): IPresentation[] {
		return Object.values(this.presentationsById);
	}

	public get currentUserPresentations(): IPresentation[] {
		const currentUserId: string = userStore.currentUser ? userStore.currentUser.sys.id : '';

		return this.presentations.filter((presentation: IPresentation): boolean => presentation.ownerId === currentUserId);
	}

	/**
	 * Current presentation settings with ovverides applied
	 */
	public get settingsWithOverrides(): IPresentationSettings {
		return {
			...this.presentation.settings,
			...(this.overridePresentationSettings ? this.overridePresentationSettings : {})
		};
	}

	/**
	 * Current presentation in structured data
	 */
	public get structuredPresentation(): IStructuredPresentation {
		// verifies if the primary contact has access to a slide
		const hasPrimaryContactAccessToSlide = (slideId: string): boolean => {
			const slideMaster: ISlideMaster = this.master.slidesById[slideId] || this.presentation.customSlidesById[slideId];

			return MarketRestrictionUtil.contactSatisfiesMarketRestrictions(
				this.primaryContact,
				slideMaster.fields.marketRestriction
			);
		};

		const mapSlide = (slideId: string): IStructuredSlide => {
			const slideMaster: ISlideMaster = this.master.slidesById[slideId] || this.presentation.customSlidesById[slideId];

			return {
				type: slideMaster.type,
				nodeType: ChapterItemType.Slide,
				title: slideMaster.fields.label || slideMaster.fields.headline || (slideMaster as ICustomSlide).headline || '',
				// eslint-disable-next-line
				thumbnail: slideMaster.fields.thumbnail ? slideMaster.fields.thumbnail.fields.asset : (slideMaster.fields.image ? slideMaster.fields.image.fields.asset : (slideMaster as ICustomSlide).asset),
				...this.slideSettingsById[slideId],
				id: slideId,
				expired: SlideUtil.isSlideUnpublished(slideMaster)
			};
		};

		return {
			chapters: this.settingsWithOverrides.chapters.map(
				(chapterSettings: IChapterSettings): IStructuredChapter => ({
					id: chapterSettings.id,
					title: this.master.chaptersById[chapterSettings.id].title,
					type: this.master.chaptersById[chapterSettings.id].type as ChapterType,
					items: this.chapterItemsByChapterId[chapterSettings.id]
						// filter slides primary contact has no access to
						.filter(
							(item: IChapterItem) => item.type === ChapterItemType.Section || hasPrimaryContactAccessToSlide(item.id)
						)
						.map(
							(item: IChapterItem): IStructuredChapterItem => {
								if (item.type === ChapterItemType.Section) {
									const section: IStructuredSection = {
										id: item.id,
										title: this.master.sectionsById[item.id].title,
										nodeType: item.type,
										slides: this.slideIdsBySectionId[item.id]
											// filter slides primary contact has no access to
											.filter(hasPrimaryContactAccessToSlide)
											.map(mapSlide)
									};

									return section;
								}

								return mapSlide(item.id);
							}
						)
				})
			)
		};
	}

	/**
	 * A dictionary of all presentationIds indicating whether they are up to date (true) or can be upgraded(false)
	 */
	public get masterFreshnessByPresentationId(): Record<string, Freshness> {
		const masterFreshnessByPresentationId: Record<string, Freshness> = {};

		Object.values(this.presentationsById).forEach((presentation: IPresentation): void => {
			const presentationMaster: IPresentationMaster | undefined = PresentationMasterUtil.findPresentationMaster(
				presentation,
				this.currentMastersByIdAndLocale
			);
			if (!presentationMaster) {
				masterFreshnessByPresentationId[presentation.id] = Freshness.Unknown;
			} else {
				masterFreshnessByPresentationId[presentation.id] =
					presentation.masterChecksum === presentationMaster.checksum ? Freshness.UpToDate : Freshness.OutOfDate;
			}
		});

		return masterFreshnessByPresentationId;
	}

	/**
	 * A dictionary of all presentationIds indicating whether they contain selected slides that have expired
	 */
	public get hasExpiredSelectedSlidesByPresentationId(): Record<string, boolean> {
		const hasExpiredSelectedSlidesByPresentationId: Record<string, boolean> = {};

		Object.values(this.presentationsById).forEach((presentation: IPresentation): void => {

			const presentationMaster: IPresentationMaster | undefined = PresentationMasterUtil.findMasterSnapshotByIdChecksumAndLocale(
				presentation,
				this.masterSnapshotsByMasterIdChecksumAndLocale
			);

			if (presentationMaster) {
				hasExpiredSelectedSlidesByPresentationId[presentation.id] = PresentationUtil.hasExpiredSelectedSlides(
					presentation,
					presentationMaster
				);
			}
			// if no presentationMaster for this presentation is available, we cannot determine expired slide states,
			// so do not flag the presentation as being expired until we know otherwise
			else {
				hasExpiredSelectedSlidesByPresentationId[presentation.id] = false;
			}
		});

		return hasExpiredSelectedSlidesByPresentationId;
	}

	/**
	 * Retrieves the next slide after currentSlide as the new currentSlide (based on order of slides in presentation)
	 * Will not have any effect when triggered in last slide of the presentation
	 */
	public get nextSlideId(): string | undefined {
		const currentSlideIndex: number = this.selectedSlideIds.findIndex(
			(slideId: string): boolean => slideId === this.currentSlideId
		);
		return currentSlideIndex < this.selectedSlideIds.length - 1
			? this.selectedSlideIds[currentSlideIndex + 1]
			: undefined;
	}

	/**
	 * Retrieves the previous slide after currentSlide as the new currentSlide (based on order of slides in presentation)
	 */
	public get previousSlideId(): string | undefined {
		const currentSlideIndex: number = this.selectedSlideIds.findIndex(
			(slideId: string): boolean => slideId === this.currentSlideId
		);
		return currentSlideIndex > 0 ? this.selectedSlideIds[currentSlideIndex - 1] : undefined;
	}

	/**
	 * Retrieves a dict of  slide type -> first selected slide id with type
	 */
	public get firstSelectedSlideIdBySlideType(): Partial<Record<SlideType, string>> {
		const firstSlideIdBySlideType: Partial<Record<SlideType, string>> = {};

		this.selectedSlideIds.forEach((slideId: string): void => {
			const slideMaster: ISlideMaster = this.master.slidesById[slideId] || this.presentation.customSlidesById[slideId];

			if (!firstSlideIdBySlideType[slideMaster.type]) firstSlideIdBySlideType[slideMaster.type] = slideId;
		});

		return firstSlideIdBySlideType;
	}

	/**
	 * Retrieves a dict of chapter id -> first selected slide id in chapter
	 */
	public get firstSelectedSlideIdByChapterId(): Record<string, string> {
		const firstSelectedSlideIdByChapterId: Record<string, string> = {};

		this.selectedChapterIds.forEach((chapterId: string): void => {
			if (this.selectedSlideIdsByChapterId[chapterId])
				[firstSelectedSlideIdByChapterId[chapterId]] = this.selectedSlideIdsByChapterId[chapterId];
		});

		return firstSelectedSlideIdByChapterId;
	}
}
